import {
  ArrowLeft,
  Download,
  Info,
  PlayCircle,
  LayoutGrid,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Redo2,
  Sun,
  Undo2,
} from 'lucide-react';
import { useState } from 'react';
import ContextSwitcher from '../workspace/ContextSwitcher';
import { parseTourSteps } from '../../lib/tour';
import { navigateToDashboard } from '../../App';
import { exportDiagramAsPng, exportDiagramAsSvg } from '../../lib/export';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useThemeStore } from '../../store/useThemeStore';

interface ArchitectureToolbarProps {
  diagramRef: React.RefObject<HTMLDivElement>;
  leftOpen: boolean;
  onToggleLeft: () => void;
  rightOpen: boolean;
  onToggleRight: () => void;
}

export default function ArchitectureToolbar({ diagramRef, leftOpen, onToggleLeft, rightOpen, onToggleRight }: ArchitectureToolbarProps) {
  const projectName = useArchitectureStore((s) => s.projectName);
  const diagramId = useArchitectureStore((s) => s.projectId) ?? '';
  const dirty = useArchitectureStore((s) => s.dirty);
  const rename = useArchitectureStore((s) => s.rename);
  const save = useArchitectureStore((s) => s.save);
  const undo = useArchitectureStore((s) => s.undo);
  const redo = useArchitectureStore((s) => s.redo);
  const canUndo = useArchitectureStore((s) => s.past.length > 0);
  const canRedo = useArchitectureStore((s) => s.future.length > 0);
  const autoLayout = useArchitectureStore((s) => s.autoLayout);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const reactFlowInstance = useDiagramViewStore((s) => s.instance);
  const readOnly = useDiagramViewStore((s) => s.readOnly);

  const [exportOpen, setExportOpen] = useState(false);

  const iconButtonClass = 'rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white';

  return (
    <div className="relative z-40 flex h-14 items-center gap-1.5 border-b border-gray-200/70 bg-white/80 px-3 backdrop-blur-xl dark:border-white/10 dark:bg-gray-950/80">
      <button onClick={navigateToDashboard} className={iconButtonClass} title="Back to dashboard">
        <ArrowLeft className="h-4 w-4" />
      </button>
      <button onClick={onToggleLeft} className={iconButtonClass} title="Toggle node palette">
        {leftOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
      </button>

      <ContextSwitcher kind="architecture" diagramId={diagramId}>
        <input
          value={projectName}
          readOnly={readOnly}
        onChange={(e) => rename(e.target.value)}
          onBlur={save}
          className="rounded-lg bg-transparent px-2 py-1 text-sm font-semibold tracking-tight hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-100 dark:hover:bg-white/10 dark:focus:bg-white/10"
          style={{ width: `${Math.max(projectName.length + 2, 10)}ch` }}
        />
      </ContextSwitcher>
      {readOnly ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">View only</span>
      ) : (
        <span className="text-xs text-gray-400 dark:text-gray-500">{dirty ? 'Unsaved' : 'Saved'}</span>
      )}

      <div className="mx-1.5 h-5 w-px bg-gray-200 dark:bg-white/10" />

      <button disabled={!canUndo || readOnly} onClick={undo} className={`${iconButtonClass} disabled:opacity-30`} title="Undo (Ctrl+Z)">
        <Undo2 className="h-4 w-4" />
      </button>
      <button disabled={!canRedo || readOnly} onClick={redo} className={`${iconButtonClass} disabled:opacity-30`} title="Redo (Ctrl+Shift+Z)">
        <Redo2 className="h-4 w-4" />
      </button>

      <div className="mx-1.5 h-5 w-px bg-gray-200 dark:bg-white/10" />

      <button
        onClick={autoLayout}
        disabled={readOnly}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
        title="Auto Layout (F)"
      >
        <LayoutGrid className="h-4 w-4" /> Auto Layout
      </button>

      <div className="relative">
        <button
          onClick={() => setExportOpen((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <Download className="h-4 w-4" /> Export
        </button>
        {exportOpen && (
          <div
            className="absolute left-0 top-full z-20 mt-2 w-48 rounded-xl border border-gray-200/80 bg-panel p-1 shadow-pop dark:border-white/10 dark:bg-gray-900"
            onMouseLeave={() => setExportOpen(false)}
          >
            <button
              className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
              onClick={async () => {
                if (diagramRef.current && reactFlowInstance) {
                  await exportDiagramAsPng(
                    diagramRef.current,
                    reactFlowInstance,
                    `${projectName}.png`,
                    theme === 'dark' ? '#030712' : '#eef0f4',
                  );
                }
                setExportOpen(false);
              }}
            >
              Diagram as PNG
            </button>
            <button
              className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
              onClick={async () => {
                if (diagramRef.current && reactFlowInstance) {
                  await exportDiagramAsSvg(
                    diagramRef.current,
                    reactFlowInstance,
                    `${projectName}.svg`,
                    theme === 'dark' ? '#030712' : '#eef0f4',
                  );
                }
                setExportOpen(false);
              }}
            >
              Diagram as SVG
            </button>
          </div>
        )}
      </div>

      <div className="flex-1" />

      <button
        onClick={() => {
          const { documentation } = useArchitectureStore.getState();
          if (parseTourSteps(documentation).length === 0) {
            useDiagramViewStore.getState().showToast('This diagram has no story yet. Add "### Step 1:" sections to its explanation.');
            return;
          }
          useDiagramViewStore.getState().openTour(0);
        }}
        className={iconButtonClass}
        title="Play the story: a guided tour of this diagram"
      >
        <PlayCircle className="h-4 w-4" />
      </button>
      <button
        onClick={() => {
          if (!rightOpen) onToggleRight();
          useDiagramViewStore.getState().requestDocs(true);
        }}
        className={iconButtonClass}
        title="About this diagram: read or write its explanation"
      >
        <Info className="h-4 w-4" />
      </button>

      <button onClick={toggleTheme} className={iconButtonClass} title="Toggle dark mode">
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <button onClick={onToggleRight} className={iconButtonClass} title="Toggle properties panel">
        {rightOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
      </button>
    </div>
  );
}
